import { hash } from '@ember/helper';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import {
  PrismBadge,
  PrismInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBase, {
  type HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature,
} from 'lumen/components/hotel-and-market/revenue-insight/base/segmentation-block-title-base';
import HotelAndMarketRevenueInsightLoadingStateTitle from 'lumen/components/hotel-and-market/revenue-insight/loading-state-title';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import { MAPPING_TYPE_RATE_CODE } from 'lumen/utils/constants/pms-mapping-constants';

interface HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitleSignature extends HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature {
  Args: HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature['Args'] & {
    extra?: {
      hasHotelsThatMapByRateCode: boolean;
    };
    isDisabled: boolean;
    params: HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBaseSignature['Args']['params'] & {
      showExpandAndSearch: boolean;
    };
  };
  Blocks: {
    default: [];
  };
}

// Remove the wrapping div so the child content is a direct child of the prism-card and can use the slots
export default class HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitle extends HotelAndMarketRevenueInsightBaseSegmentationBlockTitleBase<HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitleSignature> {
  @service declare access: Services['access'];

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  @computed(
    'args.extra.hasHotelsThatMapByRateCode',
    'hotelAndMarketService.hotel.revenueInsightMappingSegmentBreakdown',
    'riSelections.{isMultiProperty,isSingleProperty,isTargetActive}',
    'segmentationName',
  )
  get infoText(): string {
    if (this.segmentationName === RI_BREAKDOWNS.BOOKING_LEAD_TIME) {
      if (this.access.hasFeature('pms.transient_group_split')) {
        return this.intl.t('revenue_insight.booking_window_explanation');
      }
      return this.intl.t('revenue_insight.booking_window_explanation.no_split');
    }
    if (
      this.segmentationName === RI_BREAKDOWNS.SEGMENT &&
      this.riSelections.isTargetActive
    ) {
      if (
        this.riSelections.isSingleProperty &&
        this.hotelAndMarketService.hotel &&
        this.hotelAndMarketService.hotel
          .revenueInsightMappingSegmentBreakdown === MAPPING_TYPE_RATE_CODE
      ) {
        return this.intl.t(
          'revenue_insight.segment_by_rate_code_target_unavailable',
        );
      }
      if (
        this.riSelections.isMultiProperty &&
        this.args.extra?.hasHotelsThatMapByRateCode
      ) {
        return this.intl.t(
          'revenue_insight.multi.segment_by_rate_code_target_unavailable',
        );
      }
    }

    return '';
  }

  get showBreakdownDetail(): boolean {
    // Hide breakdown detail for cancellation leadtime in percentage view
    return !(
      this.riSelections.isCancellationsPercentageView &&
      this.segmentationName === RI_BREAKDOWNS.CANCELLATION_LEAD_TIME
    );
  }

  <template>
    <div class="card-title-header card-header">
      {{#if @params.isLoading}}
        <div class="title-box flex items-center">
          <HotelAndMarketRevenueInsightLoadingStateTitle />
        </div>
      {{else if this.isSearchActive}}
        <div class="flex flex-grow">
          <PrismInput
            @autofocus={{true}}
            @iconAfter={{hash name="search"}}
            @onInput={{this.updateSearchValueTask.perform}}
            @placeholder={{this.searchPlaceholder}}
            @removePadding={{true}}
            @value={{this.tempSearchValue}}
            {{outsideClickHandler this.disableSearch}}
          />
        </div>
      {{else}}
        <div class="title-box flex items-center gap-100">
          {{yield}}
          {{#if this.infoText}}
            <GeneralIconWithTooltip
              @side="top"
              @tooltipContent={{this.infoText}}
            />
          {{/if}}
          {{#if @isDisabled}}
            <span>
              <PrismBadge
                @iconName="circle-information-outline"
                @iconPosition="right"
                @label={{t "general.filtering_unavailable"}}
              />
              <PrismTooltip
                @text={{t "revenue_insight.filtering_unavailable.explanation"}}
              />
            </span>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
